/**
 * Error code to user-friendly message mapping.
 *
 * This file maps backend error codes to user-friendly messages.
 * Error codes are defined in backend/api/v1/error_codes.py.
 *
 * When adding new error codes, update both files:
 * - backend: backend/api/v1/error_codes.py
 * - frontend: frontend/src/lib/errors.ts
 */

// ============================================================================
// Types
// ============================================================================

export interface ApiError {
  code: string;
  message: string;
  details?: {
    fields?: Array<{ field: string; message: string }>;
    retry_after?: number;
  };
}

export interface ErrorActions {
  primary?: {
    label: string;
    to: string;
    onClick?: () => void;
  };
  secondary?: {
    label: string;
    to: string;
    onClick?: () => void;
  };
}

// ============================================================================
// Error Code to Message Mapping
// ============================================================================

/**
 * User-friendly error messages for each error code.
 * These messages are displayed to users in the UI.
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // ========================================================================
  // Authentication Errors
  // ========================================================================
  EMAIL_ALREADY_EXISTS: 'This email is already registered. Would you like to log in instead?',
  INVALID_CREDENTIALS: 'Invalid email or password. Please check your credentials and try again.',
  WEAK_PASSWORD: 'Password must be at least 8 characters with uppercase, lowercase, and numbers.',
  INVALID_EMAIL_FORMAT: 'Please enter a valid email address.',
  TERMS_NOT_ACCEPTED: 'You must accept the Terms of Service to continue.',
  PASSWORDS_DO_NOT_MATCH: 'Passwords do not match. Please check and try again.',
  INVALID_RESET_TOKEN: 'Invalid password reset token. Please request a new reset link.',
  RESET_TOKEN_EXPIRED: 'Password reset link has expired. Please request a new reset link.',

  // ========================================================================
  // Rate Limiting
  // ========================================================================
  RATE_LIMIT_EXCEEDED: 'Too many attempts. Please wait a few minutes and try again.',

  // ========================================================================
  // Validation Errors
  // ========================================================================
  VALIDATION_ERROR: 'Please correct the errors below.',
  MISSING_REQUIRED_FIELD: 'Required fields are missing. Please fill in all required fields.',
  INVALID_FIELD_FORMAT: 'Please check the format of your input.',

  // ========================================================================
  // Agent Errors
  // ========================================================================
  AGENT_NAME_REQUIRED: 'Please enter an agent name.',
  AGENT_LIMIT_REACHED: "You've reached your agent limit. Upgrade your plan to create more agents.",
  INVALID_SYSTEM_PROMPT: 'System prompt must be between 10 and 5000 characters.',
  SKILL_NOT_FOUND: 'Selected skill not found. Please choose a valid skill.',
  CHANNEL_NOT_CONFIGURED: 'Please configure this channel first.',
  AGENT_NOT_FOUND: 'Agent not found. It may have been deleted.',

  // ========================================================================
  // Dashboard / Analytics Errors
  // ========================================================================
  DASHBOARD_NOT_CONFIGURED: 'Dashboard analytics are being configured. Check back soon.',
  ANALYTICS_NOT_AVAILABLE: 'Analytics are currently unavailable. Please try again later.',

  // ========================================================================
  // Billing Errors
  // ========================================================================
  BILLING_NOT_AVAILABLE: 'Billing management is currently unavailable.',
  PAYMENT_REQUIRED: 'Payment required. Please update your billing information.',

  // ========================================================================
  // Authorization Errors
  // ========================================================================
  UNAUTHORIZED: 'You must be logged in to access this resource.',
  FORBIDDEN: 'You do not have permission to access this resource.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',

  // ========================================================================
  // Resource Errors
  // ========================================================================
  NOT_FOUND: 'The requested resource was not found.',
  ALREADY_EXISTS: 'This resource already exists.',
  CONFLICT: 'There is a conflict with the current state of this resource.',

  // ========================================================================
  // Server Errors
  // ========================================================================
  INTERNAL_ERROR: 'Something went wrong. Please try again or contact support.',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again later.',

  // ========================================================================
  // Legacy / Generic Fallback
  // ========================================================================
  HTTP_ERROR: 'Something went wrong. Please try again or contact support.',
  DEFAULT: 'Something went wrong. Please try again or contact support.',
};

// ============================================================================
// Error Actions Mapping
// ============================================================================

/**
 * Action buttons for specific error codes.
 * These provide users with helpful next steps.
 */
export const ERROR_ACTIONS: Record<string, ErrorActions | null> = {
  EMAIL_ALREADY_EXISTS: {
    primary: { label: 'Log In', to: '/login' },
    secondary: { label: 'Reset Password', to: '/forgot-password' },
  },
  AGENT_LIMIT_REACHED: {
    primary: { label: 'View Plans', to: '/settings#agency' },
    secondary: { label: 'Contact Sales', to: '/contact' },
  },
  SESSION_EXPIRED: {
    primary: { label: 'Log In', to: '/login' },
  },
  UNAUTHORIZED: {
    primary: { label: 'Log In', to: '/login' },
  },
  PAYMENT_REQUIRED: {
    primary: { label: 'Update Billing', to: '/settings#billing' },
  },
  DASHBOARD_NOT_CONFIGURED: {
    primary: { label: 'Go to Settings', to: '/settings' },
  },
};

// ============================================================================
// Error Message Helpers
// ============================================================================

/**
 * Get user-friendly error message from error code.
 * Falls back to DEFAULT message if code not found.
 */
export function getErrorMessage(error: ApiError | string | null): string {
  if (!error) return ERROR_MESSAGES.DEFAULT;

  const code = typeof error === 'string' ? error : error.code;
  return ERROR_MESSAGES[code] || ERROR_MESSAGES.DEFAULT;
}

/**
 * Get error actions for a specific error code.
 * Returns null if no actions are defined.
 */
export function getErrorActions(error: ApiError | string | null): ErrorActions | null {
  if (!error) return null;

  const code = typeof error === 'string' ? error : error.code;
  return ERROR_ACTIONS[code] || null;
}

/**
 * Format retry_after seconds into human-readable time.
 * Example: 300 -> "5 minutes"
 */
export function formatRetryTime(seconds: number | undefined): string {
  if (!seconds) return 'a few minutes';

  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}

/**
 * Get rate limit error message with formatted wait time.
 */
export function getRateLimitMessage(retryAfter?: number): string {
  const waitTime = formatRetryTime(retryAfter);
  return `Too many attempts. Please wait ${waitTime} and try again.`;
}

// ============================================================================
// Validation Error Parsing
// ============================================================================

/**
 * Validation error field interface.
 */
export interface ValidationErrorField {
  field: string;
  message: string;
}

/**
 * Parsed validation errors record.
 */
export type ValidationErrors = Record<string, string>;

/**
 * Parse validation errors from API error response.
 * Extracts field-level errors from response.details.fields.
 */
export function getValidationErrors(error: ApiError | null): ValidationErrors {
  if (!error || error.code !== 'VALIDATION_ERROR') {
    return {};
  }

  const fields = error.details?.fields || [];
  return fields.reduce((acc, fieldError) => {
    // Normalize field names (camelCase to user-friendly if needed)
    const fieldName = fieldError.field;
    acc[fieldName] = fieldError.message;
    return acc;
  }, {} as ValidationErrors);
}

/**
 * Check if an error is a specific error code.
 */
export function isErrorCode(error: ApiError | null, code: string): boolean {
  return error?.code === code;
}

/**
 * Check if an error is a validation error with field details.
 */
export function hasValidationErrors(error: ApiError | null): boolean {
  return error?.code === 'VALIDATION_ERROR' &&
    !!error?.details?.fields &&
    error.details.fields.length > 0;
}

/**
 * Check if an error is a rate limit error with retry_after.
 */
export function isRateLimitError(error: ApiError | null): boolean {
  return error?.code === 'RATE_LIMIT_EXCEEDED';
}

/**
 * Get retry_after seconds from error response.
 */
export function getRetryAfter(error: ApiError | null): number | undefined {
  return error?.details?.retry_after;
}

// ============================================================================
// Error Summary Helpers
// ============================================================================

/**
 * Get a summary of validation errors.
 * Returns "Please correct the errors below" or a custom message.
 */
export function getValidationSummary(error: ApiError | null): string {
  if (!error) return ERROR_MESSAGES.VALIDATION_ERROR;

  if (error.message && error.message !== ERROR_MESSAGES.VALIDATION_ERROR) {
    return error.message;
  }

  return ERROR_MESSAGES.VALIDATION_ERROR;
}

/**
 * Get the total count of validation errors.
 */
export function getValidationErrorCount(error: ApiError | null): number {
  if (!hasValidationErrors(error)) return 0;
  return error?.details?.fields?.length || 0;
}

// ============================================================================
// Error Display Helpers
// ============================================================================

/**
 * Check if error should show action buttons.
 */
export function hasErrorActions(error: ApiError | null): boolean {
  const actions = getErrorActions(error);
  return !!(actions?.primary || actions?.secondary);
}

/**
 * Check if error is a "not found" type (404).
 */
export function isNotFoundError(error: ApiError | null): boolean {
  return error?.code === 'NOT_FOUND' ||
    error?.code === 'AGENT_NOT_FOUND' ||
    error?.code === 'DASHBOARD_NOT_CONFIGURED';
}

/**
 * Check if error is an "unauthorized" type (401).
 */
export function isUnauthorizedError(error: ApiError | null): boolean {
  return error?.code === 'UNAUTHORIZED' ||
    error?.code === 'INVALID_CREDENTIALS' ||
    error?.code === 'SESSION_EXPIRED';
}

/**
 * Check if error is a "forbidden" type (403).
 */
export function isForbiddenError(error: ApiError | null): boolean {
  return error?.code === 'FORBIDDEN';
}

// ============================================================================
// Error Message for Display
// ============================================================================

/**
 * Get the complete error message for display.
 * Handles rate limits, validation, and generic errors.
 */
export function getDisplayMessage(error: ApiError | null): string {
  if (!error) return ERROR_MESSAGES.DEFAULT;

  // Rate limit with retry_after
  if (isRateLimitError(error)) {
    const retryAfter = getRetryAfter(error);
    return getRateLimitMessage(retryAfter);
  }

  // Use mapped message or the message from the error
  const mappedMessage = ERROR_MESSAGES[error.code];
  if (mappedMessage && mappedMessage !== ERROR_MESSAGES.DEFAULT) {
    return mappedMessage;
  }

  // Fall back to the error's own message
  return error.message || ERROR_MESSAGES.DEFAULT;
}
